import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";

import routes from "../routes/routes";

// components
import Button from "../components/Button";
import PageWrap from "../components/PageWrap";
import FieldSet from "../components/forms/FieldSet";
import InputGroup from "../components/forms/InputGroup";
import RadioButton from "../components/forms/RadioButton";

// Redux actions
import { update as updateCountry } from "../store/slices/country";
import { update as updateDocumentType } from "../store/slices/documentType";
import { update as updateSubject } from "../store/slices/subject";
import { update as updateCurrentlyCapturing } from "../store/slices/currentlyCapturing";

const StyledEm = styled.em`
  margin-top: 0.25em;
  display: block;
`;

const StyledDiv = styled.div`
  height: 100%;
  padding-top: 1.375em;
`;

function ChooseDocument() {
  const dispatch = useDispatch();

  // Redux selectors
  const documentType = useSelector((state) => state.documentType.value);
  const country = useSelector((state) => state.country.value);

  /**
   * Once country and documentType are selected
   * set the subject and currentlyCapturing values
   */
  useEffect(() => {
    if (country && documentType) {
      if (documentType === "drivers-license") {
        dispatch(updateSubject("DL_FRONT"));
        dispatch(updateCurrentlyCapturing("driver's license front"));
      }
      if (documentType === "passport") {
        dispatch(updateSubject("PASSPORT"));
        dispatch(updateCurrentlyCapturing("passport"));
      }
    }
  }, [country, documentType, dispatch]);

  // Input events
  const pickCountry = (event) => {
    dispatch(updateCountry(event.target.value));
  };

  const pickDocumentType = (event) => {
    dispatch(updateDocumentType(event.target.value));
  };

  // Render document type radio button
  const documentTypeButton = (type, preferred) => {
    if (type === "drivers-license") {
      return (
        <RadioButton
          name="documentType"
          id="drivers-license"
          checked={documentType === "drivers-license"}
          value="drivers-license"
          onChange={(event) => pickDocumentType(event)}
        >
          <div>
            Driver's License or State ID
            {preferred ? <StyledEm>(Preferred)</StyledEm> : null}
          </div>
        </RadioButton>
      );
    }
    if (type === "passport") {
      return (
        <RadioButton
          name="documentType"
          id="passport"
          checked={documentType === "passport"}
          value="passport"
          onChange={(event) => pickDocumentType(event)}
        >
          <div>
            Passport
            {preferred ? <StyledEm>(Preferred)</StyledEm> : null}
          </div>
        </RadioButton>
      );
    }
  };

  // render document type radio buttons
  const documentTypeButtons = () => {
    if (country) {
      if (country === "usa-or-canada") {
        return (
          <FieldSet legend="What kind of ID document would you like to submit?">
            <InputGroup>
              {documentTypeButton("drivers-license", true)}
              {documentTypeButton("passport")}
            </InputGroup>
          </FieldSet>
        );
      }
      if (country === "other-country") {
        return (
          <FieldSet legend="What kind of ID document would you like to submit?">
            <InputGroup>
              {documentTypeButton("passport", true)}
              {documentTypeButton("drivers-license")}
            </InputGroup>
          </FieldSet>
        );
      }
    } else {
      return (
        <FieldSet
          legend="What kind of ID document would you like to submit?"
          style={{ visibility: "hidden" }}
        >
          <InputGroup>
            {documentTypeButton("drivers-license", true)}
            {documentTypeButton("passport")}
          </InputGroup>
        </FieldSet>
      );
    }
    return null;
  };

  return (
    <PageWrap noHeader>
      <StyledDiv>
        <FieldSet legend="Where is your ID from?" border={country}>
          <InputGroup>
            <RadioButton
              name="country"
              id="usa-or-canada"
              checked={country === "usa-or-canada"}
              value="usa-or-canada"
              onChange={(event) => pickCountry(event)}
            >
              USA or Canada
            </RadioButton>
            <RadioButton
              name="country"
              id="other-country"
              checked={country === "other-country"}
              value="other-country"
              onChange={(event) => pickCountry(event)}
            >
              Other country
            </RadioButton>
          </InputGroup>
        </FieldSet>
        {documentTypeButtons()}
        {country === "other-country" && documentType === "drivers-license" ? (
          <p role="alert" style={{ padding: "0 1.19em" }}>
            Please note that not all ID types are supported. In particular,
            identification documents with non-Latinate characters cannot be
            scanned.
          </p>
        ) : null}
      </StyledDiv>
      <Button
        as={Link}
        to={`${routes.instructions}`}
        disabled={!(country && documentType)}
      >
        Start Scanning
      </Button>
    </PageWrap>
  );
}

export default ChooseDocument;
